import { useSuspenseQuery } from '@tanstack/react-query';
import type { BroadcastCheckResultDto } from '@upme/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@upme/ui/components/alert-dialog';
import { Button } from '@upme/ui/components/button';
import { Label } from '@upme/ui/components/label';
import { Switch } from '@upme/ui/components/switch';
import { useState } from 'react';
import { toast } from 'sonner';
import { broadcastErrorText, readBroadcastError } from '../errors';
import { useCheckBroadcast, useSetBroadcastActive, useUnbindBroadcastChat } from '../mutations';
import { cityBroadcastOptions } from '../queries';
import { BroadcastChatForm } from './broadcast-chat-form';
import { BroadcastCheckResult } from './broadcast-check-result';
import { BroadcastQueue } from './broadcast-queue';
import { BroadcastSetupHint } from './broadcast-setup-hint';

/** Трансляция событий города в Telegram-чат (#146): привязка, проверка прав, тумблер, очередь. */
export function CityBroadcastBlock({ cityId }: { cityId: string }) {
  const { data: state } = useSuspenseQuery(cityBroadcastOptions(cityId));
  const check = useCheckBroadcast(cityId);
  const setActive = useSetBroadcastActive(cityId);
  const unbind = useUnbindBroadcastChat(cityId);
  // Последний результат проверки в этой сессии (ответ мутации или check из тела 422).
  const [lastCheck, setLastCheck] = useState<BroadcastCheckResultDto | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Счётчик для key формы: повторный chat_migrated с тем же текстом должен перемонтировать форму.
  const [formKey, setFormKey] = useState(0);

  const onBound = (result: BroadcastCheckResultDto) => {
    setLastCheck(result);
    setFormOpen(false);
    setFormError(null);
  };

  // check в теле 422 — показываем недостающие права. chat_migrated — новый id надо вставить в форму,
  // поэтому текст держим под её полем, а не в исчезающем тосте. Остальное (503, 422 без check) — тост.
  const showActionError = async (err: unknown, fallback: string) => {
    const apiErr = await readBroadcastError(err);
    if (apiErr?.check) {
      setLastCheck(apiErr.check);
      return;
    }
    if (apiErr?.code === 'chat_migrated') {
      setFormError(apiErr.message);
      setFormKey((k) => k + 1);
      setFormOpen(true);
      return;
    }
    toast.error(broadcastErrorText(apiErr, fallback));
  };

  const onCheck = async () => {
    try {
      setLastCheck(await check.mutateAsync());
    } catch (err) {
      await showActionError(err, 'Не удалось проверить связь');
    }
  };

  const onToggle = async (active: boolean) => {
    try {
      await setActive.mutateAsync(active);
      // Успешное включение делает прежний результат проверки (с недостающими правами) устаревшим.
      if (active) setLastCheck(null);
    } catch (err) {
      await showActionError(err, 'Не удалось переключить автопостинг');
    }
  };

  const onToggleForm = () => {
    // При закрытии сбрасываем ошибку, чтобы повторное открытие не показало устаревший текст.
    if (formOpen) setFormError(null);
    setFormOpen(!formOpen);
  };

  const onUnbind = async () => {
    try {
      await unbind.mutateAsync();
      setLastCheck(null);
      setFormOpen(false);
      setFormError(null);
      toast.success('Чат отвязан');
    } catch (err) {
      toast.error(broadcastErrorText(await readBroadcastError(err), 'Не удалось отвязать чат'));
    }
  };

  const switchId = `broadcast-active-${cityId}`;
  // Пока PATCH идёт — показываем целевое положение; при ошибке state не менялся, тумблер вернётся сам.
  const checked =
    setActive.isPending && setActive.variables !== undefined ? setActive.variables : state.isActive;

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Трансляция в Telegram-чат</h2>

      {state.chat === null ? (
        <>
          <BroadcastSetupHint defaultOpen />
          <BroadcastChatForm cityId={cityId} onBound={onBound} />
        </>
      ) : (
        <>
          <div>
            <p className="font-medium">{state.chat.title ?? 'Без названия'}</p>
            <p className="font-mono text-xs text-muted-foreground">{state.chat.id}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={check.isPending} onClick={() => void onCheck()}>
              Проверить связь
            </Button>
            <Button variant="outline" onClick={onToggleForm}>
              Сменить чат
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">Отвязать</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Отвязать чат «{state.chat.title ?? state.chat.id}»?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Посты, уже отправленные в чат, останутся и будут удаляться при отмене событий.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Отмена</AlertDialogCancel>
                  <AlertDialogAction onClick={() => void onUnbind()}>Отвязать</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {formOpen && (
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm text-muted-foreground">
                Смена чата выключит автопостинг: очередь нового чата начнётся с событий, опубликованных после
                включения.
              </p>
              <BroadcastChatForm key={formKey} cityId={cityId} initialError={formError} onBound={onBound} />
            </div>
          )}

          {lastCheck && <BroadcastCheckResult check={lastCheck} />}

          {state.disabledReason !== null && (
            <div
              role="alert"
              className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive"
            >
              Трансляция выключена автоматически: {state.disabledReason}. Восстановите права бота, нажмите
              «Проверить связь» и снова включите автопостинг.
            </div>
          )}

          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Switch
                id={switchId}
                checked={checked}
                disabled={setActive.isPending}
                onCheckedChange={(next) => void onToggle(next)}
              />
              <Label htmlFor={switchId}>Автопостинг</Label>
            </div>
            <p className="text-sm text-muted-foreground">
              Посты уходят по одному раз в 30 минут с 10:00 до 20:00 по времени города ({state.timezone}). В
              очередь попадают события, опубликованные после включения. Повторное включение того же чата
              отправит и накопившееся за паузу.
            </p>
          </div>

          <BroadcastQueue state={state} />
        </>
      )}
    </section>
  );
}
